/* =========================================================
   SHIPPING ETA + CITY/ZIP UPDATER — ported verbatim (vanilla JS, no
   jQuery dependency) from the live theme's assets/custom-script.js.
   Only the unrelated jQuery-dependent reward-calculator code at the
   bottom of that file was left out — this is the shipping/ETA logic
   only. Uses Day.js (loaded dynamically). Detects city via
   localStorage → IP lookup fallback. Lets the customer override via
   ZIP (zippopotam.us). Targets #custom_message / .custom_message on
   product pages, .collection-delivery-message on collection cards.
   ========================================================= */

function llmLoadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const LLM_CITY_STORAGE_KEY = "shippingCity";
const LLM_ZIP_STORAGE_KEY = "shippingZip";

let llmCustomerCity = "";

async function llmFetchCustomerCity() {
  try {
    const savedCity = localStorage.getItem(LLM_CITY_STORAGE_KEY);
    if (savedCity) {
      llmCustomerCity = savedCity;
      return;
    }

    const res = await fetch("https://ipapi.co/json/");
    if (!res.ok) return;

    const data = await res.json();
    llmCustomerCity = data.city || "";

    if (llmCustomerCity) localStorage.setItem(LLM_CITY_STORAGE_KEY, llmCustomerCity);
  } catch (e) {
    console.error("Failed to get customer city", e);
  }
}

function llmPluralize(n, singular, plural) {
  return n === 1 ? singular : plural;
}

function llmFormatCountdown(days, hours, minutes) {
  const parts = [];

  if (days > 0) {
    parts.push(`${days} ${llmPluralize(days, "day", "days")}`);
  }

  if (hours > 0) {
    parts.push(`${hours} hrs`);
  }

  parts.push(`${minutes} mins`);

  return parts.join(" ");
}

async function llmSetupShippingMessage() {
  try {
    await llmLoadScript("https://cdn.jsdelivr.net/npm/dayjs@1/dayjs.min.js");
    await llmLoadScript("https://cdn.jsdelivr.net/npm/dayjs@1/plugin/utc.js");
    await llmLoadScript("https://cdn.jsdelivr.net/npm/dayjs@1/plugin/timezone.js");
    await llmLoadScript("https://cdn.jsdelivr.net/npm/dayjs@1/plugin/duration.js");

    dayjs.extend(dayjs_plugin_utc);
    dayjs.extend(dayjs_plugin_timezone);
    dayjs.extend(dayjs_plugin_duration);
  } catch (error) {
    console.error("Failed to load required scripts:", error);
    return;
  }

  const ORDER_CUTOFF_HOUR = 15;
  const TZ = "America/New_York";

  await llmFetchCustomerCity();

  function renderShippingMessages() {
    const now = dayjs().tz(TZ);
    const hrs = now.hour();
    const mins = now.minute();
    const dayOfWeek = now.day();
    const isAfterCutoff = hrs >= ORDER_CUTOFF_HOUR;

    const citySuffix = llmCustomerCity
      ? ` to <span class="shipping-city" data-city="${llmCustomerCity}">${llmCustomerCity}</span>`
      : "";

    let estDeliveryStartObj = null;
    let estDeliveryEndObj = null;

    let productMsgHtml = "";

    const isExtendedWeekend =
      dayOfWeek === 6 || dayOfWeek === 0 || (dayOfWeek === 5 && isAfterCutoff);

    let nextCutoff = now.clone().hour(ORDER_CUTOFF_HOUR).minute(0).second(0);

    if (isExtendedWeekend) {
      let daysUntilMonday = 0;
      if (dayOfWeek === 5) daysUntilMonday = 3;
      else if (dayOfWeek === 6) daysUntilMonday = 2;
      else if (dayOfWeek === 0) daysUntilMonday = 1;

      nextCutoff = now
        .add(daysUntilMonday, "day")
        .hour(ORDER_CUTOFF_HOUR)
        .minute(0)
        .second(0);
    } else if (isAfterCutoff && dayOfWeek !== 5) {
      nextCutoff = now.add(1, "day").hour(ORDER_CUTOFF_HOUR).minute(0).second(0);
    }

    if (isExtendedWeekend) {
      const remaining = dayjs.duration(nextCutoff.diff(now));
      const daysRemaining = Math.floor(remaining.asDays());
      const hoursRemaining = remaining.hours();
      const minutesRemaining = remaining.minutes();

      const shipDate = now.clone().startOf("day").day(1);

      estDeliveryStartObj = shipDate.add(1, "day");
      estDeliveryEndObj = shipDate.add(3, "day");

      const startFull = estDeliveryStartObj.format("dddd");
      const endFull = estDeliveryEndObj.format("dddd");

      const countdown = llmFormatCountdown(daysRemaining, hoursRemaining, minutesRemaining);

      productMsgHtml = `<strong>Ships Monday</strong>${citySuffix}, if ordered in the next <span class="free-ship-time-left">${countdown}</span><br><strong>Estimated delivery</strong>: ${startFull} to ${endFull}`;
    } else if (!isAfterCutoff) {
      const remainingHours = ORDER_CUTOFF_HOUR - hrs - (mins > 0 ? 1 : 0);
      const remainingMinutes = 60 - mins;

      estDeliveryStartObj = now.add(1, "day");
      estDeliveryEndObj = now.add(3, "day");

      if (dayOfWeek === 5) estDeliveryEndObj = now.add(4, "day");

      const startFull = estDeliveryStartObj.format("dddd");
      const endFull = estDeliveryEndObj.format("dddd");

      const countdown = `${remainingHours} hrs ${remainingMinutes} mins`;

      productMsgHtml = `<strong>Ships today</strong>${citySuffix}, if ordered in the next <span class="free-ship-time-left">${countdown}</span><br><strong>Estimated delivery</strong>: ${startFull} to ${endFull}`;
    } else {
      const remaining = dayjs.duration(nextCutoff.diff(now));
      const hoursRemaining = remaining.hours();
      const minutesRemaining = remaining.minutes();

      const shipDate = now.add(1, "day");

      if (dayOfWeek === 4) {
        estDeliveryStartObj = shipDate.add(1, "day");
        estDeliveryEndObj = shipDate.add(4, "day");
      } else if (dayOfWeek === 3) {
        estDeliveryStartObj = shipDate.add(1, "day");
        estDeliveryEndObj = shipDate.add(4, "day");
      } else {
        estDeliveryStartObj = shipDate.add(1, "day");
        estDeliveryEndObj = shipDate.add(3, "day");
      }

      const startFull = estDeliveryStartObj.format("dddd");
      const endFull = estDeliveryEndObj.format("dddd");

      const countdown = `${hoursRemaining} hrs ${minutesRemaining} mins`;

      productMsgHtml = `<strong>Ships tomorrow</strong>${citySuffix}, if ordered in the next <span class="free-ship-time-left">${countdown}</span><br><strong>Estimated delivery</strong>: ${startFull} to ${endFull}`;
    }

    const msgTargets = document.querySelectorAll("#custom_message, .custom_message");
    if (msgTargets.length) {
      msgTargets.forEach((el) => {
        el.style.display = "block";
        /* Only rewrite when the message text actually changed. This runs on a
           1s interval, but the text only changes when the countdown minute
           (or city) changes - roughly once a minute. Rewriting every tick
           needlessly recreates the (i) icon + popup, which drops any open
           CSS-hover / tap-hover tooltip mid-read (the mobile "won't stay
           open"). Keyed off a JS property so it survives Appstle reassigning
           the mount's className. */
        if (el.__llmLastMsg === productMsgHtml) return;
        el.__llmLastMsg = productMsgHtml;
        el.innerHTML = `
          <span class="custom-shipping-message"${
            estDeliveryStartObj && estDeliveryEndObj
              ? ` data-eta-start="${estDeliveryStartObj.format("ddd, MMM D")}" data-eta-end="${estDeliveryEndObj.format("ddd, MMM D")}"`
              : ""
          }>${productMsgHtml}</span>
          <span class="info-ship">
            <img src="https://cdn.shopify.com/s/files/1/0700/5519/8950/files/info-msg.svg?v=1729838946" alt="Shipping information">
          </span>
          <span class="popup-box-message">
            <button type="button" class="popup-box-message__close" aria-label="Close"><span class="llm-tip-close-x" aria-hidden="true">&times;</span> close</button>
            <p>We try to keep all products in stock at all of our warehouses, but sometimes we might be out of stock of a product in the warehouse nearest to you. In these instances, shipping times might take longer than 1-3 business days.</p>
            <p>Thanks for your understanding.</p>
          </span>
        `;
      });
    }

    if (estDeliveryStartObj && estDeliveryEndObj) {
      const startShort = estDeliveryStartObj.format("ddd");
      const endShort = estDeliveryEndObj.format("ddd");

      const cityHtml = llmCustomerCity
        ? ` in <span class="shipping-city" data-city="${llmCustomerCity}">${llmCustomerCity}</span>`
        : "";

      // Shortened per owner feedback ("needs to be shortened up
      // some") — was "Get it Wed-Fri in Ambler, with FREE 1-3 day
      // delivery on orders $49+", now leads with the arrival window
      // and drops straight to the free-shipping threshold.
      // ⚠️ THE TWO HALVES ARE SPANS NOW, NOT SEPARATED BY <br>. Owner 2026-10-21 asked for one
      // centred line on phones, where the card's bottom row is full width (~316px) and fits it
      // easily. A <br> is a hard break that CSS cannot undo, so it forced two lines everywhere.
      // Desktop list view deliberately KEEPS two lines — there this sits in the ~200px quick-add
      // column, where one line would wrap at an arbitrary point anyway. The breakpoint decides,
      // in llm-product-card.css; search .collection-delivery-message there.
      const shortMsg = llmCustomerCity
        ? `<span class="cdm__eta">Arrives <strong>${startShort}-${endShort}${cityHtml}</strong></span><span class="cdm__ship">FREE shipping $49+</span>`
        : `<span class="cdm__eta">Arrives <strong>${startShort}-${endShort}</strong></span><span class="cdm__ship">FREE shipping $49+</span>`;

      document.querySelectorAll(".collection-delivery-message").forEach((el) => {
        el.innerHTML = shortMsg;
      });
    }
  }

  async function handleZipUpdate() {
    const zipInput = document.getElementById("shipping-zip-input");
    const errorEl = document.getElementById("shipping-zip-error");
    if (!zipInput || !errorEl) return;

    const zip = (zipInput.value || "").trim();
    errorEl.style.display = "none";
    errorEl.textContent = "";

    if (!zip) return;

    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!res.ok) throw new Error("Invalid ZIP");

      const data = await res.json();
      const newCity = data?.places?.[0]?.["place name"];

      if (!newCity) throw new Error("Invalid ZIP");

      llmCustomerCity = newCity;

      localStorage.setItem(LLM_CITY_STORAGE_KEY, llmCustomerCity);
      localStorage.setItem(LLM_ZIP_STORAGE_KEY, zip);

      const popup = document.getElementById("shipping-city-popup");
      if (popup) popup.style.display = "none";

      renderShippingMessages();
    } catch (err) {
      console.error(err);
      errorEl.textContent = "Please enter a valid ZIP code.";
      errorEl.style.display = "block";
    }
  }

  function initShippingUI() {
    if (!document.getElementById("shipping-city-popup")) {
      document.body.insertAdjacentHTML(
        "beforeend",
        `
        <div id="shipping-city-popup" class="shipping-city-popup" style="display:none;">
          <div class="shipping-city-popup-title">Not your city? Enter ZIP:</div>
          <input type="text" id="shipping-zip-input" maxlength="5" class="shipping-zip-input" inputmode="numeric" />
          <button type="button" id="shipping-zip-submit" class="shipping-zip-submit">Update</button>
          <div id="shipping-zip-error" class="shipping-zip-error" style="display:none;"></div>
        </div>
        `
      );
    }

    renderShippingMessages();
    setInterval(renderShippingMessages, 1000);

    /* Exposed globally so llm-autoship-toggle.js can call it
       immediately after inserting a fresh .custom_message mount point
       (e.g. when Appstle's widget re-renders the option cards on a
       variant change, wiping out the previously-injected in-card
       delivery estimate along with everything else). Without this,
       the newly-recreated mount sat empty for up to the full 1-second
       interval before this function's own periodic re-run filled it
       back in, reading as a visible "disappear and reappear" flicker
       on every variant change. */
    window.llmRenderShippingMessages = renderShippingMessages;

    document.addEventListener("click", (e) => {
      const popup = document.getElementById("shipping-city-popup");
      const cityEl = e.target.closest(".shipping-city");

      if (cityEl && popup) {
        const rect = cityEl.getBoundingClientRect();
        popup.style.top = window.scrollY + rect.bottom + "px";
        popup.style.left = window.scrollX + rect.left + "px";
        popup.style.display = "block";

        const zipInput = document.getElementById("shipping-zip-input");
        const errorEl = document.getElementById("shipping-zip-error");

        if (zipInput) {
          zipInput.value = localStorage.getItem(LLM_ZIP_STORAGE_KEY) || "";
          zipInput.focus();
        }
        if (errorEl) {
          errorEl.style.display = "none";
          errorEl.textContent = "";
        }

        e.stopPropagation();
        return;
      }

      if (popup && popup.style.display === "block" && !popup.contains(e.target) && !cityEl) {
        popup.style.display = "none";
      }
    });

    document.addEventListener("click", (e) => {
      if (e.target && e.target.id === "shipping-zip-submit") {
        handleZipUpdate();
      }
    });

    document.addEventListener("keydown", (e) => {
      const zipInput = document.getElementById("shipping-zip-input");
      if (e.key === "Enter" && e.target === zipInput) {
        e.preventDefault();
        handleZipUpdate();
      }
      if (e.key === "Escape") {
        const popup = document.getElementById("shipping-city-popup");
        if (popup) popup.style.display = "none";
      }
    });

    /* The shipping-note tooltip (the (i) that reveals "We try to keep all
       products in stock...") is shown purely by the .info-ship:hover CSS rule
       - hover on desktop, tap-hover on mobile - like the live theme (an
       earlier JS toggle was removed: it fought the CSS popup, making two
       appear on one tap). The ONE bit of JS still needed: the in-card (i) is
       a plain <span> nested inside an Appstle purchase-option control, so on
       iOS a tap neither applies :hover (spans aren't interactive) nor is safe
       (it would select the option / the card link). This capture-phase
       handler makes the icon interactive (so iOS applies :hover and reveals
       the tooltip) and swallows the tap so it can't trigger the option/link.
       On the cart the icon isn't inside a control, so this is a harmless
       no-op there. */
    document.addEventListener(
      "click",
      (e) => {
        const info = e.target.closest(".info-ship");
        if (info) {
          /* Re-tapping the (i) clears a prior manual dismiss so it reopens. */
          const popup = info.parentElement && info.parentElement.querySelector(".popup-box-message");
          if (popup) popup.classList.remove("is-closed");
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    /* Close (X) inside the shipping tooltip. The popup is shown by the
       .info-ship:hover CSS rule; adding .is-closed (display:none !important)
       lets the user dismiss it explicitly (mainly for touch, where there's no
       hover-off). Hovering / re-tapping the (i) clears it again (above + below). */
    document.addEventListener("click", (e) => {
      const x = e.target.closest(".popup-box-message__close");
      if (!x) return;
      e.preventDefault();
      e.stopPropagation();
      const popup = x.closest(".popup-box-message");
      if (popup) popup.classList.add("is-closed");
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    });
    document.addEventListener("mouseover", (e) => {
      const info = e.target.closest(".info-ship");
      if (!info) return;
      const popup = info.parentElement && info.parentElement.querySelector(".popup-box-message");
      if (popup) popup.classList.remove("is-closed");
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initShippingUI);
  } else {
    initShippingUI();
  }
}

llmSetupShippingMessage();
